// Out-of-market locations: every RESTORE office outside the five main
// markets (Chicago, Dallas, Denver, Charlotte, New York). Decided 18 Oct 2026
// (Jordan, via Chris): these show as an area served by the main locations,
// with a virtual consultation, not as a clinic. No phone number.
//
// Main-market offices are NOT listed here and keep their cards. An office is
// main market when the Acuity booking account (owner 24213130) has a
// calendar for it, or its market hub page lists its city in the hub's area
// list (restore-chicago, restore-dallas, restore-denver, restore-charlotte),
// or the New York state hub lists it.
//
// Addresses: Acuity has a calendar for Indianapolis only, so Indianapolis
// uses the Acuity address. The rest use the site's store-locator record
// (restore-locations.ts), which /locations/ and the state hubs already show.
// Where a city page showed a different building, the store-locator record
// wins. ZIP codes marked "USPS" are not in any site record.
//
// Add, move or close an out-of-market office here, not in a page file.

export const MAIN_MARKETS = ["Chicago", "Dallas", "Denver", "Charlotte", "New York"] as const;
export const MAIN_MARKETS_LABEL = MAIN_MARKETS.join(", ");
export const VIRTUAL_CONSULT_PATH = "/schedule-a-consult/virtual/";

export type ServiceArea = {
  /** The city the card says it serves: "Serving <name>, <stateName>". */
  name: string;
  stateName: string;
  street: string;
  /** City in the postal address (Phoenix and Las Vegas are served from nearby suburbs). */
  locality: string;
  region: string;
  postalCode: string;
};

export const SERVICE_AREAS = {
  scottsdale: {
    name: "Scottsdale",
    stateName: "Arizona",
    street: "7150 East Camelback Rd, Suite 444",
    locality: "Scottsdale",
    region: "AZ",
    postalCode: "85251",
  },
  phoenix: {
    name: "Phoenix",
    stateName: "Arizona",
    street: "7150 East Camelback Rd, Suite 444",
    locality: "Scottsdale",
    region: "AZ",
    postalCode: "85251",
  },
  "beverly-hills": {
    name: "Beverly Hills",
    stateName: "California",
    street: "8383 Wilshire Blvd, Suite 800",
    locality: "Beverly Hills",
    region: "CA",
    postalCode: "90211",
  },
  "newport-beach": {
    name: "Newport Beach",
    stateName: "California",
    street: "895 Dove Street, 3rd Floor",
    locality: "Newport Beach",
    region: "CA",
    postalCode: "92660",
  },
  "san-diego": {
    name: "San Diego",
    stateName: "California",
    street: "350 10th Ave, Suite 1000",
    locality: "San Diego",
    region: "CA",
    postalCode: "92101",
  },
  "san-francisco": {
    name: "San Francisco",
    stateName: "California",
    street: "75 Broadway, Suite 202",
    locality: "San Francisco",
    region: "CA",
    postalCode: "94111",
  },
  "colorado-springs": {
    name: "Colorado Springs",
    stateName: "Colorado",
    street: "102 S. Tejon Street, Suite 1100",
    locality: "Colorado Springs",
    region: "CO",
    postalCode: "80903",
  },
  "washington-dc": {
    name: "Washington",
    stateName: "D.C.",
    street: "1875 I Street NW, 5th Floor",
    locality: "Washington",
    region: "DC",
    postalCode: "20006",
  },
  "boca-raton": {
    name: "Boca Raton",
    stateName: "Florida",
    street: "2255 Glades Road, Suite 324A",
    locality: "Boca Raton",
    region: "FL",
    postalCode: "33431",
  },
  jacksonville: {
    name: "Jacksonville",
    stateName: "Florida",
    street: "4651 Salisbury Rd, Suite 400",
    locality: "Jacksonville",
    region: "FL",
    postalCode: "32256",
  },
  miami: {
    name: "Miami",
    stateName: "Florida",
    street: "80 SW 8th St, Suite 2000",
    locality: "Miami",
    region: "FL",
    postalCode: "33130",
  },
  tampa: {
    name: "Tampa",
    stateName: "Florida",
    street: "8875 Hidden River Pkwy, Suite 300",
    locality: "Tampa",
    region: "FL",
    postalCode: "33637",
  },
  atlanta: {
    name: "Atlanta",
    stateName: "Georgia",
    street: "1201 Peachtree Street, Suite 200",
    locality: "Atlanta",
    region: "GA",
    postalCode: "30361",
  },
  // Acuity calendar 8884039.
  indianapolis: {
    name: "Indianapolis",
    stateName: "Indiana",
    street: "450 E. 96th St, Suite 500",
    locality: "Indianapolis",
    region: "IN",
    postalCode: "46240",
  },
  "new-orleans": {
    name: "New Orleans",
    stateName: "Louisiana",
    street: "1615 Poydras St, Suite 900",
    locality: "New Orleans",
    region: "LA",
    postalCode: "70112",
  },
  // USPS ZIP.
  baltimore: {
    name: "Baltimore",
    stateName: "Maryland",
    street: "4000 East Pratt St, 8th Floor",
    locality: "Baltimore",
    region: "MD",
    postalCode: "21224",
  },
  // The store-locator record dropped the leading zero ("2116").
  boston: {
    name: "Boston",
    stateName: "Massachusetts",
    street: "75 Arlington Street, Suite 500",
    locality: "Boston",
    region: "MA",
    postalCode: "02116",
  },
  novi: {
    name: "Novi",
    stateName: "Michigan",
    street: "39555 Orchard Hill Place, Suite 600",
    locality: "Novi",
    region: "MI",
    postalCode: "48375",
  },
  edina: {
    name: "Edina",
    stateName: "Minnesota",
    street: "5201 Eden Ave, Suite 300",
    locality: "Edina",
    region: "MN",
    postalCode: "55436",
  },
  "kansas-city": {
    name: "Kansas City",
    stateName: "Missouri",
    street: "2300 Main Street, Suite 900",
    locality: "Kansas City",
    region: "MO",
    postalCode: "64108",
  },
  "st-louis": {
    name: "St. Louis",
    stateName: "Missouri",
    street: "2 Cityplace Dr, 2nd Floor",
    locality: "St. Louis",
    region: "MO",
    postalCode: "63005",
  },
  "las-vegas": {
    name: "Las Vegas",
    stateName: "Nevada",
    street: "871 Coronado Center Drive, Suite 200",
    locality: "Henderson",
    region: "NV",
    postalCode: "89052",
  },
  // USPS ZIPs for the three New Jersey offices.
  neptune: {
    name: "Neptune",
    stateName: "New Jersey",
    street: "3600 Route 66",
    locality: "Neptune",
    region: "NJ",
    postalCode: "07753",
  },
  newark: {
    name: "Newark",
    stateName: "New Jersey",
    street: "One Gateway Center",
    locality: "Newark",
    region: "NJ",
    postalCode: "07102",
  },
  paramus: {
    name: "Paramus",
    stateName: "New Jersey",
    street: "140 E Ridgewood Avenue",
    locality: "Paramus",
    region: "NJ",
    postalCode: "07652",
  },
  raleigh: {
    name: "Raleigh",
    stateName: "North Carolina",
    street: "4509 Creedmoor Rd, Suite 201",
    locality: "Raleigh",
    region: "NC",
    postalCode: "27612",
  },
  cincinnati: {
    name: "Cincinnati",
    stateName: "Ohio",
    street: "300 E. Business Way, Suite 200",
    locality: "Cincinnati",
    region: "OH",
    postalCode: "45241",
  },
  columbus: {
    name: "Columbus",
    stateName: "Ohio",
    street: "100 E. Campus View Boulevard, Suite 250",
    locality: "Columbus",
    region: "OH",
    postalCode: "43235",
  },
  independence: {
    name: "Independence",
    stateName: "Ohio",
    street: "6100 Oak Tree Boulevard, Suite 200",
    locality: "Independence",
    region: "OH",
    postalCode: "44131",
  },
  "oklahoma-city": {
    name: "Oklahoma City",
    stateName: "Oklahoma",
    street: "101 Park Ave, Suite 1300",
    locality: "Oklahoma City",
    region: "OK",
    postalCode: "73102",
  },
  portland: {
    name: "Portland",
    stateName: "Oregon",
    street: "205 SE Spokane St, Suite 300",
    locality: "Portland",
    region: "OR",
    postalCode: "97202",
  },
  philadelphia: {
    name: "Philadelphia",
    stateName: "Pennsylvania",
    street: "325-41 Chestnut Street, Suite 800",
    locality: "Philadelphia",
    region: "PA",
    postalCode: "19106",
  },
  pittsburgh: {
    name: "Pittsburgh",
    stateName: "Pennsylvania",
    street: "322 North Shore Drive, Building 1B, Suite 200",
    locality: "Pittsburgh",
    region: "PA",
    postalCode: "15212",
  },
  nashville: {
    name: "Nashville",
    stateName: "Tennessee",
    street: "3200 West End Ave, Suite 500",
    locality: "Nashville",
    region: "TN",
    postalCode: "37203",
  },
  austin: {
    name: "Austin",
    stateName: "Texas",
    street: "300 Beardsley Lane, Bldg C, Suite 101",
    locality: "Austin",
    region: "TX",
    postalCode: "78746",
  },
  houston: {
    name: "Houston",
    stateName: "Texas",
    street: "2929 Allen Pkwy, Suite 200",
    locality: "Houston",
    region: "TX",
    postalCode: "77019",
  },
  "san-antonio": {
    name: "San Antonio",
    stateName: "Texas",
    street: "700 N St Mary's St, Suite 1400",
    locality: "San Antonio",
    region: "TX",
    postalCode: "78205",
  },
  "salt-lake-city": {
    name: "Salt Lake City",
    stateName: "Utah",
    street: "222 S Main St, 5th Floor",
    locality: "Salt Lake City",
    region: "UT",
    postalCode: "84101",
  },
  "virginia-beach": {
    name: "Virginia Beach",
    stateName: "Virginia",
    street: "780 Lynnhaven Pkwy, Suite 400",
    locality: "Virginia Beach",
    region: "VA",
    postalCode: "23452",
  },
  milwaukee: {
    name: "Milwaukee",
    stateName: "Wisconsin",
    street: "250 East Wisconsin Avenue, Suite 1800",
    locality: "Milwaukee",
    region: "WI",
    postalCode: "53202",
  },
} satisfies Record<string, ServiceArea>;

export type ServiceAreaKey = keyof typeof SERVICE_AREAS;

/** Store-locator slugs (restore-locations.ts) that differ from the key here. */
const SLUG_ALIASES: Record<string, ServiceAreaKey> = { henderson: "las-vegas" };

/** The out-of-market entry for a store-locator slug, or undefined for a main-market office. */
export function serviceAreaForSlug(slug: string): ServiceArea | undefined {
  const key = (SLUG_ALIASES[slug] ?? slug) as ServiceAreaKey;
  return Object.prototype.hasOwnProperty.call(SERVICE_AREAS, key) ? SERVICE_AREAS[key] : undefined;
}

export function servingLabel(a: ServiceArea): string {
  return `Serving ${a.name}, ${a.stateName}`;
}

export function serviceAreaCityLine(a: ServiceArea): string {
  return `${a.locality}, ${a.region} ${a.postalCode}`;
}
